import React, { useState, useEffect } from "react";
import {
  AiOutlineFolderAdd,
  AiOutlineGift,
  AiOutlineMenu,
  AiOutlineClose,
  AiOutlineLogout,
} from "react-icons/ai";
import { FiPackage, FiShoppingBag } from "react-icons/fi";
import { RxDashboard } from "react-icons/rx";
import { CiSettings } from "react-icons/ci";
import {
  HiOutlineReceiptRefund,
  HiOutlineCurrencyDollar,
} from "react-icons/hi";
import { MdOutlineLocalOffer } from "react-icons/md";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BiMessageSquareDetail } from "react-icons/bi";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../../../server";
import { logoutSeller } from "../../../redux/actions/user";

const DashboardSideBar = ({ active }) => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [conversationsCount, setConversationsCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { orders } = useSelector((state) => state.order);
  const { products } = useSelector((state) => state.products);
  const { seller } = useSelector((state) => state.seller);

  const logoutHandler = async () => {
    try {
      await dispatch(logoutSeller());
      toast.success("Logged out successfully!");
      navigate("/shop-login");
      setIsMobileSidebarOpen(false);
    } catch {
      toast.error("Logout failed. Please try again.");
    }
  };

  const ordersCount = orders ? orders.length : 0;
  const productsCount = products ? products.length : 0;
  const refundRequestsCount = orders
    ? orders.filter((o) => o.status === "Processing refund" || o.status === "Refund Success").length
    : 0;

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        if (seller?._id) {
          const res = await axios.get(`${server}/conversation/get-all-conversation-seller/${seller._id}`, { withCredentials: true });
          setConversationsCount(res.data.conversations?.length || 0);
        }
      } catch { setConversationsCount(0); }
    };
    fetchConversations();
  }, [seller]);

  useEffect(() => { setIsMobileSidebarOpen(false); }, [location]);

  const menuItems = [
    { id: 1, title: "Dashboard", link: "/dashboard", icon: RxDashboard, badge: null },
    { id: 2, title: "All Orders", link: "/dashboard-orders", icon: FiShoppingBag, badge: ordersCount > 0 ? ordersCount : null },
    { id: 3, title: "All Products", link: "/dashboard-products", icon: FiPackage, badge: productsCount > 0 ? productsCount : null },
    { id: 4, title: "Create Product", link: "/dashboard-create-product", icon: AiOutlineFolderAdd, badge: null },
    { id: 5, title: "All Events", link: "/dashboard-events", icon: MdOutlineLocalOffer, badge: null },
    { id: 6, title: "All Coupons", link: "/dashboard-coupouns", icon: AiOutlineGift, badge: null },
    { id: 7, title: "Messages", link: "/dashboard-messages", icon: BiMessageSquareDetail, badge: conversationsCount > 0 ? conversationsCount : null },
    { id: 8, title: "Refund Requests", link: "/dashboard-refunds", icon: HiOutlineReceiptRefund, badge: refundRequestsCount > 0 ? refundRequestsCount : null },
    { id: 9, title: "Withdraw Money", link: "/dashboard-withdraw-money", icon: HiOutlineCurrencyDollar, badge: null },
    { id: 10, title: "Shop Settings", link: "/settings", icon: CiSettings, badge: null },
  ];

  const NavItem = ({ item, mobile = false }) => {
    const IconComponent = item.icon;
    const isActive = active === item.id;
    return (
      <Link
        to={item.link}
        onClick={() => mobile && setIsMobileSidebarOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 transition-all duration-150 group ${
          isActive
            ? "bg-black text-white"
            : "text-secondary-600 hover:bg-secondary-100 hover:text-black"
        }`}
      >
        <IconComponent size={16} className="flex-shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wide flex-1 truncate">{item.title}</span>
        {item.badge && (
          <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold ${isActive ? "bg-white text-black" : "bg-black text-white"}`}>
            {item.badge > 99 ? "99+" : item.badge}
          </span>
        )}
      </Link>
    );
  };

  const SidebarContent = ({ mobile = false }) => (
    <div className="flex flex-col h-full w-full">
      <div className="bg-black px-5 py-4 flex items-center justify-between flex-shrink-0">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo (10).png" alt="Logo" className="h-7 w-auto invert brightness-200 object-contain" />
        </Link>
        {mobile && (
          <button onClick={() => setIsMobileSidebarOpen(false)} className="text-white hover:text-secondary-300 transition-colors">
            <AiOutlineClose size={18} />
          </button>
        )}
      </div>

      {seller && (
        <div className="px-4 py-3 border-b border-secondary-200 bg-secondary-50 flex-shrink-0">
          <p className="text-[10px] uppercase tracking-widest text-secondary-400 font-semibold">Vendor</p>
          <p className="text-sm font-bold text-black truncate mt-0.5">{seller.name}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <div className={`w-1.5 h-1.5 rounded-full ${seller.approvalStatus === "approved" ? "bg-green-500" : "bg-yellow-500"}`} />
            <span className="text-[10px] text-secondary-500 uppercase tracking-wider">{seller.approvalStatus || "pending"}</span>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto py-2">
        {menuItems.map((item) => <NavItem key={`${item.id}-${item.link}`} item={item} mobile={mobile} />)}
      </div>

      <div className="border-t border-secondary-200 p-4 flex-shrink-0">
        <button
          onClick={logoutHandler}
          className="flex items-center gap-3 w-full px-3 py-2.5 text-secondary-600 hover:bg-secondary-100 hover:text-black transition-colors group"
        >
          <AiOutlineLogout size={16} className="flex-shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wide">Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="hidden lg:flex flex-col fixed left-0 top-0 h-screen w-64 bg-white border-r border-secondary-200 z-20">
        <SidebarContent />
      </div>

      <div className="lg:hidden fixed top-0 left-0 right-0 bg-black z-30 flex items-center justify-between px-4 py-3">
        <Link to="/">
          <img src="/logo (10).png" alt="Logo" className="h-8 w-auto invert brightness-200 object-contain" />
        </Link>
        <button onClick={() => setIsMobileSidebarOpen(true)} className="text-white">
          <AiOutlineMenu size={22} />
        </button>
      </div>

      {isMobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsMobileSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl">
            <SidebarContent mobile />
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardSideBar;
